import React, { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { DragDropContext, Droppable, Draggable } from "@hello-pangea/dnd";
import Header from "../components/Header";
import Sidebar from "../components/Sidebar";
import axiosInstance from "../api/axiosInstance";
import { formatDateForDisplay } from "../utils/formatters";
import { useToast } from "../context/ToastContext";
import { humanizeError } from "../utils/userFriendlyError";

const COLUMNS = [
  {
    id: "docusign_initiated",
    label: "Docusign Process Completed",
    description: "Households where the Docusign envelope has been signed"
  },
  {
    id: "submitted_to_custodian",
    label: "Submitted to Custodian",
    description: "Signed documents have been submitted to the destination custodian for processing."
  },
  {
    id: "transfer_in_progress",
    label: "Transfer In Progress",
    description: "The custodian is actively processing the account transfer."
  },
  {
    id: "transfer_rejected",
    label: "Transfer Rejected",
    description: "The custodian rejected the transfer. Review the rejection reason and resubmit."
  },
  {
    id: "partially_transferred",
    label: "Partially Transferred",
    description: "Some accounts in this household have transferred successfully, but others are still pending."
  },
  {
    id: "completed",
    label: "Completed",
    description: "All accounts have been fully transferred and verified at the destination custodian."
  }
];

const COLUMN_COLORS = {
  bg: "bg-emerald-50", border: "border-emerald-200", headerBg: "bg-emerald-100",
  text: "text-emerald-800", dot: "bg-emerald-500", badge: "bg-emerald-500", ring: "ring-emerald-300"
};

function TransitionTracker({ user }) {
  const { toast } = useToast();
  const { projectId } = useParams();
  const navigate = useNavigate();

  const [project, setProject] = useState(null);
  const [households, setHouseholds] = useState([]);
  const [loading, setLoading] = useState(true);
  const [updating, setUpdating] = useState(false);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [tooltipColumn, setTooltipColumn] = useState(null);

  const fetchProject = useCallback(async () => {
    try {
      const response = await axiosInstance.get(`/projects/${projectId}`);
      setProject(response.data);
    } catch (error) {
      console.error("Error fetching project:", error);
    }
  }, [projectId]);

  const fetchHouseholds = useCallback(async () => {
    try {
      const response = await axiosInstance.get("/households/manual-tracker", {
        params: { project_id: projectId }
      });
      const items = (response.data.households || []).map(h => ({
        ...h,
        id: h.household_id
      }));
      setHouseholds(items);
    } catch (error) {
      console.error("Error fetching households:", error);
    } finally {
      setLoading(false);
    }
  }, [projectId]);

  useEffect(() => {
    fetchProject();
    fetchHouseholds();
  }, [fetchProject, fetchHouseholds]);

  const getColumnHouseholds = (columnId) => {
    return households.filter(h => h.status === columnId);
  };

  const toggleSelect = (id) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const toggleSelectAll = (columnId) => {
    const columnHHs = getColumnHouseholds(columnId);
    const columnIds = columnHHs.map(h => h.id);
    const allSelected = columnIds.length > 0 && columnIds.every(id => selectedIds.has(id));

    setSelectedIds(prev => {
      const next = new Set(prev);
      if (allSelected) {
        columnIds.forEach(id => next.delete(id));
      } else {
        columnIds.forEach(id => next.add(id));
      }
      return next;
    });
  };

  const updateHouseholdStatuses = async (householdIds, newStatus) => {
    setUpdating(true);
    try {
      await axiosInstance.put(`/households/bulk-status-update?project_id=${projectId}`, {
        household_ids: householdIds,
        status: newStatus
      });
      setHouseholds(prev =>
        prev.map(h => householdIds.includes(h.id) ? { ...h, status: newStatus } : h)
      );
      setSelectedIds(prev => {
        const next = new Set(prev);
        householdIds.forEach(id => next.delete(id));
        return next;
      });
    } catch (error) {
      console.error("Error updating household statuses:", error);
      toast.error(humanizeError(error));
    } finally {
      setUpdating(false);
    }
  };

  const onDragEnd = (result) => {
    const { destination, source, draggableId } = result;
    if (!destination || destination.droppableId === source.droppableId) return;

    const newStatus = destination.droppableId;
    const draggedId = draggableId;

    const sourceColumnHHs = getColumnHouseholds(source.droppableId);
    const selectedInSource = sourceColumnHHs.filter(h => selectedIds.has(h.id));

    let idsToMove;
    if (selectedInSource.length > 0 && selectedIds.has(draggedId)) {
      idsToMove = selectedInSource.map(h => h.id);
    } else {
      idsToMove = [draggedId];
    }

    updateHouseholdStatuses(idsToMove, newStatus);
  };

  const isAllSelected = (columnId) => {
    const columnHHs = getColumnHouseholds(columnId);
    return columnHHs.length > 0 && columnHHs.every(h => selectedIds.has(h.id));
  };

  const selectedCountInColumn = (columnId) => {
    return getColumnHouseholds(columnId).filter(h => selectedIds.has(h.id)).length;
  };

  if (loading) {
    return (
      <div className="flex flex-col min-h-screen bg-gradient-to-br from-emerald-50/30 via-white to-blue-50/20">
        <Header user={user} />
        <div className="flex flex-1">
          <Sidebar />
          <main className="flex-1 p-8 flex items-center justify-center">
            <div className="text-center">
              <div className="relative w-20 h-20 mx-auto mb-6">
                <div className="absolute inset-0 border-4 border-emerald-200 rounded-full"></div>
                <div className="absolute inset-0 border-4 border-transparent border-t-emerald-600 rounded-full animate-spin"></div>
              </div>
              <p className="text-gray-600 font-medium text-lg">Loading transition tracker...</p>
            </div>
          </main>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col min-h-screen bg-gradient-to-br from-emerald-50/30 via-white to-blue-50/20">
      <Header user={user} />
      <div className="flex flex-1">
        <Sidebar />
        <main className="flex-1 p-8 bg-gradient-to-b from-transparent to-emerald-50/10 overflow-x-auto">
          <div className="w-full">

            {/* Project Header */}
            <div className="mb-8 relative">
              <div className="absolute inset-0 bg-gradient-to-r from-emerald-500/5 via-transparent to-blue-500/5 rounded-3xl blur-3xl"></div>
              <div className="relative bg-white/80 backdrop-blur-sm rounded-3xl p-8 border border-emerald-100/50 shadow-xl">
                <button
                  onClick={() => navigate(`/project/${projectId}`)}
                  className="mb-6 flex items-center gap-2 text-emerald-600 hover:text-emerald-700 font-semibold transition-all hover:gap-3 group"
                >
                  <svg className="w-5 h-5 group-hover:-translate-x-1 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                  </svg>
                  Back to Project
                </button>

                <div className="flex items-center gap-4 mb-3">
                  <div className="flex-1">
                    <h1 className="text-3xl font-extrabold bg-gradient-to-r from-gray-900 via-emerald-700 to-gray-900 bg-clip-text text-transparent mb-2">
                      Post-Signing Transition Tracker
                    </h1>
                    <p className="text-gray-600 text-lg font-medium">{project?.title}</p>
                  </div>
                  {project?.status && (
                    <span className={`badge px-3 py-1 font-medium ${
                      project.status === 'completed' ? 'badge-success' :
                      project.status === 'initiated' || project.status === 'created' ? 'badge-info' :
                      'badge-warning'
                    }`}>
                      {project.status === 'created' ? 'Ongoing' : project.status.charAt(0).toUpperCase() + project.status.slice(1)}
                    </span>
                  )}
                </div>

                {/* Project metadata row */}
                <div className="mt-4 pt-4 border-t border-emerald-200/50">
                  <div className="flex items-center gap-6 flex-wrap">
                    <div className="flex items-center gap-3">
                      <h3 className="text-xs font-bold text-gray-500 uppercase tracking-wider">Destination Custodian</h3>
                      <div className="px-3 py-2 rounded-lg text-sm font-semibold bg-emerald-50 text-emerald-700">
                        {project?.custodian || 'Fidelity'}
                      </div>
                    </div>
                    <div className="h-4 w-px bg-gray-300"></div>
                    <div className="flex items-center gap-3">
                      <h3 className="text-xs font-bold text-gray-500 uppercase tracking-wider">Destination Firm</h3>
                      <div className="px-3 py-2 rounded-lg text-sm font-semibold bg-emerald-50 text-emerald-700">
                        {project?.destination_firm || 'N/A'}
                      </div>
                    </div>
                    {project?.target_end_date && (
                      <>
                        <div className="h-4 w-px bg-gray-300"></div>
                        <div className="flex items-center gap-3">
                          <h3 className="text-xs font-bold text-gray-500 uppercase tracking-wider">Target End Date</h3>
                          <div className="px-3 py-2 rounded-lg text-sm font-semibold bg-amber-50 text-amber-700">
                            {formatDateForDisplay(project.target_end_date)}
                          </div>
                        </div>
                      </>
                    )}
                    <div className="h-4 w-px bg-gray-300"></div>
                    <div className="flex items-center gap-3">
                      <h3 className="text-xs font-bold text-gray-500 uppercase tracking-wider">Total Households</h3>
                      <div className="px-3 py-2 rounded-lg text-sm font-semibold bg-emerald-50 text-emerald-700">
                        {households.length}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Updating overlay */}
            {updating && (
              <div className="fixed inset-0 bg-black/30 backdrop-blur-sm z-50 flex items-center justify-center">
                <div className="bg-white rounded-2xl p-8 shadow-2xl border border-emerald-100 flex flex-col items-center gap-4">
                  <div className="relative w-16 h-16">
                    <div className="absolute inset-0 border-4 border-emerald-200 rounded-full"></div>
                    <div className="absolute inset-0 border-4 border-transparent border-t-emerald-600 rounded-full animate-spin"></div>
                  </div>
                  <p className="text-gray-700 font-semibold text-lg">Updating status...</p>
                </div>
              </div>
            )}

            {/* Kanban Board */}
            <DragDropContext onDragEnd={onDragEnd}>
              <div className="flex gap-3 pb-4">
                {COLUMNS.map(column => {
                  const colors = COLUMN_COLORS;
                  const columnHHs = getColumnHouseholds(column.id);
                  const allSel = isAllSelected(column.id);
                  const selCount = selectedCountInColumn(column.id);

                  return (
                    <div
                      key={column.id}
                      className={`flex-1 min-w-0 rounded-2xl border ${colors.border} ${colors.bg} flex flex-col max-h-[calc(100vh-340px)]`}
                    >
                      {/* Column header */}
                      <div className={`px-4 py-3 ${colors.headerBg} rounded-t-2xl border-b ${colors.border}`}>
                        <div className="flex items-center justify-between mb-1">
                          <div className="flex items-center gap-2">
                            <div className={`w-2.5 h-2.5 rounded-full ${colors.dot}`}></div>
                            <h3 className={`text-sm font-bold ${colors.text}`}>{column.label}</h3>
                            <div className="relative">
                              <button
                                onClick={() => setTooltipColumn(tooltipColumn === column.id ? null : column.id)}
                                className={`p-0.5 rounded-full hover:bg-white/50 transition-colors ${colors.text} opacity-60 hover:opacity-100`}
                              >
                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                                </svg>
                              </button>
                              {tooltipColumn === column.id && (
                                <div className="absolute left-1/2 -translate-x-1/2 top-full mt-2 z-50 w-64 p-3 rounded-xl bg-gray-900 text-white text-xs font-medium shadow-xl leading-relaxed">
                                  {column.description}
                                  <div className="absolute -top-1 left-1/2 -translate-x-1/2 w-2 h-2 bg-gray-900 rotate-45"></div>
                                </div>
                              )}
                            </div>
                          </div>
                          <span className={`text-xs font-bold ${colors.text} ${colors.headerBg} px-2 py-0.5 rounded-full`}>
                            {columnHHs.length}
                          </span>
                        </div>

                        {columnHHs.length > 0 && (
                          <div className="flex items-center gap-2 mt-2">
                            <label className="flex items-center gap-1.5 cursor-pointer text-xs font-medium text-gray-600 hover:text-gray-800 transition-colors">
                              <input
                                type="checkbox"
                                checked={allSel}
                                onChange={() => toggleSelectAll(column.id)}
                                className="w-3.5 h-3.5 rounded text-emerald-600"
                              />
                              {allSel ? "Deselect all" : "Select all"}
                            </label>
                            {selCount > 0 && (
                              <span className="text-xs font-semibold text-gray-500">
                                ({selCount} selected)
                              </span>
                            )}
                          </div>
                        )}
                      </div>

                      {/* Droppable area */}
                      <Droppable droppableId={column.id}>
                        {(provided, snapshot) => (
                          <div
                            ref={provided.innerRef}
                            {...provided.droppableProps}
                            className={`flex-1 overflow-y-auto p-3 space-y-2 transition-colors duration-200 ${
                              snapshot.isDraggingOver ? `ring-2 ${colors.ring} ring-inset bg-white/60` : ""
                            }`}
                            style={{ minHeight: "80px" }}
                          >
                            {columnHHs.length === 0 && !snapshot.isDraggingOver && (
                              <div className="flex flex-col items-center justify-center py-8 text-gray-400">
                                <svg className="w-8 h-8 mb-2 opacity-40" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2-2v-5m16 0h-2.586a1 1 0 00-.707.293l-2.414 2.414a1 1 0 01-.707.293h-3.172a1 1 0 01-.707-.293l-2.414-2.414A1 1 0 006.586 13H4" />
                                </svg>
                                <p className="text-xs font-medium">No households here</p>
                              </div>
                            )}

                            {columnHHs.map((hh, index) => {
                              const isSelected = selectedIds.has(hh.id);
                              return (
                                <Draggable key={hh.id} draggableId={hh.id} index={index}>
                                  {(provided, snapshot) => (
                                    <div
                                      ref={provided.innerRef}
                                      {...provided.draggableProps}
                                      {...provided.dragHandleProps}
                                      onClick={(e) => {
                                        if (e.target.type === 'checkbox') return;
                                        toggleSelect(hh.id);
                                      }}
                                      className={`rounded-xl border p-3 transition-all duration-150 cursor-grab active:cursor-grabbing
                                        ${snapshot.isDragging
                                          ? "shadow-xl scale-[1.03] bg-white border-emerald-300 ring-2 ring-emerald-200"
                                          : isSelected
                                            ? `bg-white border-emerald-400 shadow-md ring-2 ring-emerald-200`
                                            : "bg-white border-gray-200 shadow-sm hover:shadow-md hover:border-gray-300"
                                        }
                                      `}
                                    >
                                      <div className="flex items-start gap-2">
                                        <input
                                          type="checkbox"
                                          checked={isSelected}
                                          onChange={() => toggleSelect(hh.id)}
                                          className="w-4 h-4 rounded text-emerald-600 mt-0.5 flex-shrink-0"
                                        />
                                        <div className="flex-1 min-w-0">
                                          <p className="text-sm font-bold text-gray-900 truncate">
                                            {hh.name || 'Unnamed Household'}
                                          </p>

                                        </div>
                                      </div>
                                      {isSelected && selectedIds.size > 1 && (
                                        <div className="mt-2 pt-2 border-t border-emerald-100">
                                          <span className="text-[10px] font-semibold text-emerald-600 uppercase tracking-wider">
                                            Part of {Array.from(selectedIds).filter(id => getColumnHouseholds(column.id).some(h => h.id === id)).length} selected
                                          </span>
                                        </div>
                                      )}
                                    </div>
                                  )}
                                </Draggable>
                              );
                            })}
                            {provided.placeholder}
                          </div>
                        )}
                      </Droppable>
                    </div>
                  );
                })}
              </div>
            </DragDropContext>
          </div>
        </main>
      </div>
    </div>
  );
}

export default TransitionTracker;
